import { CheckIcon } from '@blueprinthq/joy'
import { Box, Button, Flex, List, ListItem, Text } from '@chakra-ui/react'
import { CircleCheck } from '@components/icons'
import { useInvoiceManagement, useSessionManagement } from '@context'
import { formatDollarAmount } from '@utilities'
import React from 'react'

const StatusIndicator = ({ isCompleted }: { isCompleted: boolean }) => (
  <Box
    w="16px"
    h="16px"
    mr="12px"
    mt="2px"
    display="flex"
    alignItems="center"
    justifyContent="center"
    flexShrink={0}
  >
    {isCompleted ? (
      <CircleCheck fill="#2D54E8" width="16px" height="16px" />
    ) : (
      <Box w="4px" h="4px" bg="black" borderRadius="50%" />
    )}
  </Box>
)

const FinalizeSessionCard = ({
  switchToTab
}: {
  switchToTab: (tab: string) => void
}) => {
  const { isNoteLocked, progressNote } = useSessionManagement()
  const { handleFinalizeSession, isFinalizing, isFinalized, invoice } =
    useInvoiceManagement()
  const lineItem = invoice?.lineItems?.[0]
  const lockedByUser = progressNote?.lockedByUser

  const formattedLockDate =
    isNoteLocked && progressNote.lockedAt
      ? new Date(progressNote.lockedAt).toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric'
        })
      : null

  let progressNoteStatusText = 'Approve, sign and lock note'

  if (isNoteLocked) {
    if (lockedByUser) {
      progressNoteStatusText = `Locked by ${lockedByUser.firstName.substring(0, 1)}. ${lockedByUser.lastName.trim()} · ${formattedLockDate}`
    } else {
      progressNoteStatusText = `Locked · ${formattedLockDate}`
    }
  }

  return (
    <Flex
      padding="32px 32px 24px 32px"
      flexDirection="column"
      alignItems="flex-start"
      alignSelf="stretch"
      borderBottom="1px solid"
      borderColor="pale_gray"
      bg="blue.40"
    >
      <Button
        colorScheme="primary"
        leftIcon={<CheckIcon fill="white" />}
        isFullWidth
        size="lg"
        mb="small"
        borderRadius="8px"
        boxShadow="0px 4px 12px rgba(0, 0, 0, 0.15)"
        onClick={handleFinalizeSession}
        minHeight="48px"
        padding="12px 24px"
        isLoading={isFinalizing}
        disabled={isFinalizing || isFinalized}
      >
        {isFinalized ? 'Session is finalized' : 'Finalize session with 1-Click'}
      </Button>
      <Flex
        flexDirection="column"
        alignItems="flex-start"
        alignSelf="stretch"
        gap="xsmall"
      >
        <Text fontWeight="bold" fontSize="xsm" mt="12px">
          {isFinalized
            ? 'Session is finalized'
            : 'Finalizing will automatically:'}
        </Text>
        <List pl="xsmall" pr="medium" spacing="6px">
          <ListItem>
            <Flex align="center">
              <StatusIndicator isCompleted={isNoteLocked} />
              <Text fontSize="xsm" lineHeight="xsm">
                {progressNoteStatusText}
                <Button
                  color="primary"
                  variant="link"
                  size="sm"
                  ml="1"
                  verticalAlign="baseline"
                  onClick={() => switchToTab('Note')}
                >
                  <Text fontSize="xsm" lineHeight="xsm" textDecoration="none">
                    {isNoteLocked ? 'View' : 'Edit'}
                  </Text>
                </Button>
              </Text>
            </Flex>
          </ListItem>
          <ListItem>
            {/* First Row: bullet + primary text */}
            <Flex align="center">
              <StatusIndicator isCompleted={isFinalized} />
              <Text fontSize="xsm" lineHeight="xsm">
                {isFinalized
                  ? 'Invoice sent to client'
                  : 'Send invoice for session'}
                <Button
                  color="primary"
                  variant="link"
                  size="sm"
                  ml="1"
                  verticalAlign="baseline"
                  onClick={() => switchToTab('Invoice')}
                >
                  <Text fontSize="xsm" lineHeight="xsm" textDecoration="none">
                    {isFinalized ? 'View' : 'Edit'}
                  </Text>
                </Button>
              </Text>
            </Flex>

            {/* Second Row */}
            {lineItem && (
              <Text fontSize="xsm" color="dark_gray" ml="28px" mt="2px">
                {lineItem.serviceTypeCode && `${lineItem.serviceTypeCode} `}
                {lineItem.description && `${lineItem.description} `}
                {lineItem.price && `· ${formatDollarAmount(lineItem.price)}`}
              </Text>
            )}
          </ListItem>
          {/* <ListItem>
          <Text fontSize="xsm" lineHeight="xsm">
            Send super-bill to client
          </Text>
        </ListItem> */}
        </List>
      </Flex>
    </Flex>
  )
}

export default FinalizeSessionCard
